import type { SavedBroker } from "@freightsimple/generated-dashboard-openapi-client/models/SavedBroker";
import type { ReactNode } from "react";
import { useRef, useState } from "react";
import { BrokersAddDialog } from "@/Features/Brokers/Dialogs/BrokersAddDialog";

// Declarative replacement for the imperative useBrokersAddBrokerDialog: callers await
// `show()` mid-flow, so a resolver ref bridges the dialog back to a promise.
// Resolves the new SavedBroker, or undefined if the user closes the dialog.
export function useBrokersAddBrokerDialog(): {
  show: () => Promise<SavedBroker | undefined>;
  dialogs: ReactNode;
} {
  const [open, setOpen] = useState(false);
  const [openCount, setOpenCount] = useState(0);
  const resolverRef = useRef<
    ((newSavedBroker: SavedBroker | undefined) => void) | undefined
  >(undefined);

  function show(): Promise<SavedBroker | undefined> {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setOpenCount((count) => count + 1);
      setOpen(true);
    });
  }

  function settle(newSavedBroker: SavedBroker | undefined) {
    resolverRef.current?.(newSavedBroker);
    resolverRef.current = undefined;
  }

  const dialogs = (
    <BrokersAddDialog
      key={`brokers-add-${openCount}`}
      open={open}
      onOpenChange={function (nowOpen) {
        setOpen(nowOpen);
        if (!nowOpen) {
          settle(undefined);
        }
      }}
      onFinished={function (newSavedBroker) {
        settle(newSavedBroker);
        setOpen(false);
      }}
    />
  );

  return { show, dialogs };
}
